<template>
  <div class="smesco-revenue">
    <div class="flex justify-between items-center pb-6 shadow-border-b mdmax:flex-col mdmax:gap-2">
      <div class="flex-1 flex justify-end items-center gap-2 mdmax:flex-auto mdmax:w-full">
        <Button buttonText="Generate Report" type="primary" :size="isMobile ? 'full' : 'regular'" @action="openGenerateModal" />
      </div>
    </div>
    
    <TableComponent :columns="columns" :list="revenues">
      <template slot="table-row" slot-scope="{ entry, column }">
        <div v-if="column.field === 'action'" class="flex gap-2">
          <Button v-if="entry.status === 'Draft'" buttonText="Approve" type="success" size="small" @action="confirmApprove(entry.id)" />
          <Button buttonText="Delete" type="delete" size="small" @action="confirmDelete(entry.id)" />
        </div>
        
        <span v-else-if="column.field === 'period'">
          {{ getMonthName(entry.month) }} {{ entry.year }}
        </span>
        
        <span v-else-if="column.field === 'revenue' || column.field === 'profit'">
          Rp {{ Number(entry[column.field]).toLocaleString('id-ID') }}
        </span>
        
        <span v-else-if="column.field === 'status'">
          <span class="px-2 py-1 rounded text-xs font-medium" :class="statusClass(entry.status)">
            {{ entry.status }}
          </span>
        </span>
        
        <span v-else-if="column.field === 'report_excel_url'">
          <a v-if="entry.report_excel_url" :href="entry.report_excel_url" target="_blank" class="text-primary-500 hover:text-primary-600 flex items-center gap-1 font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
          <span v-else class="text-neutral-400">-</span>
        </span>
        
        <span v-else>{{ entry[column.field] !== null ? entry[column.field] : '-' }}</span>
      </template>
    </TableComponent>
    
    <div class="text-neutral-500 font-medium mt-2 text-sm" v-if="!isMobile">Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}</div>
    <div class="mt-8 flex justify-center items-center mb-6" v-if="revenues && revenues.length > 0">
      <pagination :currentPage="currentPage" :totalCount="totalRecords" :pageSize="pageSize" @onPageChange="onPageChange" />
    </div>

    <!-- Modal Generate Report -->
    <Modal :modalVisible="generateModalVisible" @close="closeGenerateModal" width="450px">
      <div slot="modal-content">
        <h3 class="text-lg font-bold text-neutral-800 mb-4 border-b pb-2">Generate Report</h3>
        <div class="grid grid-cols-1 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-2">Bulan</label>
            <select v-model.number="form.month" class="w-full border border-neutral-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="" disabled>Pilih Bulan</option>
              <option v-for="(name, index) in monthNames" :key="index" :value="index + 1">{{ name }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-2">Tahun</label>
            <select v-model.number="form.year" class="w-full border border-neutral-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="" disabled>Pilih Tahun</option>
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>
        <div class="flex justify-end gap-3">
          <button @click="closeGenerateModal" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium">Batal</button>
          <button @click="generateReport" class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium" :disabled="isGenerating">
            {{ isGenerating ? 'Proses...' : 'Generate' }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Modal Confirm Approve -->
    <Modal :modalVisible="approveModalVisible" @close="closeApproveModal" width="400px">
      <div slot="modal-content" class="text-center">
        <h3 class="text-lg font-bold text-neutral-800 mb-2">Konfirmasi Publish</h3>
        <p class="text-neutral-600 mb-6">Apakah Anda yakin ingin melakukan Publish data bulan ini?</p>
        <div class="flex justify-center gap-3">
          <button @click="closeApproveModal" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium">Batal</button>
          <button @click="submitApprove" class="px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 font-medium">Publish</button>
        </div>
      </div>
    </Modal>

    <!-- Modal Confirm Delete -->
    <Modal :modalVisible="deleteModalVisible" @close="closeDeleteModal" width="400px">
      <div slot="modal-content" class="text-center">
        <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Report</h3>
        <p class="text-neutral-600 mb-6">Apakah Anda yakin ingin menghapus report data ini?</p>
        <div class="flex justify-center gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium">Batal</button>
          <button @click="submitDelete" class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium">Hapus</button>
        </div>
      </div>
    </Modal>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'
import Modal from '@/components/Modal/Modal'

export default {
  components: {
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    TableComponent: () => import(/* webpackChunkName: "Table" */ '@/components/Table/Table'),
    Pagination: () => import(/* webpackChunkName: "Pagination" */ '@/components/Pagination/Pagination'),
    Modal
  },
  data() {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i)
    }

    return {
      columns: [
        { label: 'Bulan & Tahun', field: 'period' },
        { label: 'Total Transaksi', field: 'total_transaction' },
        { label: 'Total Revenue', field: 'revenue' },
        { label: 'Total Profit', field: 'profit' },
        { label: 'Status', field: 'status' },
        { label: 'File Excel', field: 'report_excel_url' },
        { label: 'Action', field: 'action' }
      ],
      monthNames: ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"],
      yearOptions: years,
      revenues: [],
      totalRecords: 0,
      currentPage: 1,
      pageSize: 10,
      
      // Modals
      generateModalVisible: false,
      approveModalVisible: false,
      deleteModalVisible: false,
      
      itemToApprove: null,
      itemToDelete: null,
      
      form: {
        month: '',
        year: ''
      },
      isGenerating: false
    }
  },
  computed: {
    ...mapGetters('common', ['isMobile']),
    start() {
      return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1
    },
    end() {
      const lastOnPage = (this.currentPage - 1) * this.pageSize + this.pageSize
      return lastOnPage > this.totalRecords ? this.totalRecords : lastOnPage
    }
  },
  created() {
    if (!this.$route.query.page) {
      this.$router.push({ path: '/smesco/revenue', query: { page: 1 } })
      this.currentPage = 1
    } else {
      this.currentPage = parseInt(this.$route.query.page)
      this.getRevenueList()
    }
  },
  watch: {
    $route(val) {
      if (val.query.page) {
        this.currentPage = parseInt(val.query.page)
        this.getRevenueList()
      } else {
        this.$router.push({ path: '/smesco/revenue', query: { page: 1 } })
        this.currentPage = 1
      }
    }
  },
  methods: {
    ...mapActions('common', ['showLoading', 'hideLoading']),
    
    getMonthName(monthNum) {
      if (!monthNum || monthNum < 1 || monthNum > 12) return ''
      return this.monthNames[monthNum - 1]
    },
    
    statusClass(status) {
      if (status === 'Draft') return 'bg-yellow-100 text-yellow-700'
      if (status === 'Publish') return 'bg-green-100 text-green-700'
      return 'bg-gray-100 text-gray-700'
    },
    
    getRevenueList() {
      this.showLoading()
      api.smesco.revenues({
        page: this.currentPage,
        limit: this.pageSize,
        sort: 'desc'
      })
        .then(res => {
          if (res.data && res.data.data) {
            this.revenues = res.data.data.items || []
            this.totalRecords = res.data.data.pagination?.total_item || 0
          }
          this.hideLoading()
        })
        .catch(() => {
          this.hideLoading()
        })
    },
    
    onPageChange(page) {
      this.currentPage = page
      this.$router.push({ path: '/smesco/revenue', query: { page: page } })
    },
    
    // Generate Actions
    openGenerateModal() {
      this.form.month = ''
      this.form.year = ''
      this.generateModalVisible = true
    },
    closeGenerateModal() {
      this.generateModalVisible = false
    },
    generateReport() {
      if (!this.form.month || !this.form.year) {
        alert('Bulan dan Tahun harus dipilih')
        return
      }
      
      this.isGenerating = true
      this.showLoading()
      
      const payload = {
        month: this.form.month,
        year: this.form.year
      }
      
      api.smesco.createRevenue(payload)
        .then(() => {
          this.$toast?.success('Report berhasil digenerate')
          this.closeGenerateModal()
          this.getRevenueList()
        })
        .catch(err => {
          this.$toast?.error(err.response?.data?.message || 'Gagal menggenerate report atau report sudah pernah dibuat')
        })
        .finally(() => {
          this.isGenerating = false
          this.hideLoading()
        })
    },
    
    // Approve Actions
    confirmApprove(id) {
      this.itemToApprove = id
      this.approveModalVisible = true
    },
    closeApproveModal() {
      this.approveModalVisible = false
      this.itemToApprove = null
    },
    submitApprove() {
      if (!this.itemToApprove) return
      this.showLoading()
      
      api.smesco.approveRevenue(this.itemToApprove)
        .then(() => {
          this.$toast?.success('Data berhasil dipublish')
          this.getRevenueList()
        })
        .catch(err => {
          this.$toast?.error(err.response?.data?.message || 'Terjadi kesalahan saat mempublish data')
        })
        .finally(() => {
          this.hideLoading()
          this.closeApproveModal()
        })
    },
    
    // Delete Actions
    confirmDelete(id) {
      this.itemToDelete = id
      this.deleteModalVisible = true
    },
    closeDeleteModal() {
      this.deleteModalVisible = false
      this.itemToDelete = null
    },
    submitDelete() {
      if (!this.itemToDelete) return
      this.showLoading()
      
      api.smesco.deleteRevenue(this.itemToDelete)
        .then(() => {
          this.$toast?.success('Data berhasil dihapus')
          this.getRevenueList()
        })
        .catch(err => {
          this.$toast?.error(err.response?.data?.message || 'Terjadi kesalahan saat menghapus data')
        })
        .finally(() => {
          this.hideLoading()
          this.closeDeleteModal()
        })
    }
  }
}
</script>
